import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import type {
    CreateSupportEmailRequestDto,
    UpdateSupportEmailRequestDto,
} from '../types/support-email-request.types';

export const useSupportEmailRequestMutations = () => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();

    const invalidate = () => {
        queryClient.invalidateQueries({
            queryKey: [ApiConfig.queries.superAdmin.supportEmailRequests.queryKeys.list],
        });
    };

    const createMutation = useMutation({
        mutationFn: async (body: CreateSupportEmailRequestDto) => {
            const response = await axiosInstance.post(
                ApiConfig.queries.superAdmin.supportEmailRequests.endpoints.create,
                body,
            );
            return response.data;
        },
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, body }: { id: string; body: UpdateSupportEmailRequestDto }) => {
            const url = `${ApiConfig.queries.superAdmin.supportEmailRequests.endpoints.update}/${id}`;
            await axiosInstance.put(url, body);
        },
        onSuccess: (_, variables) => {
            invalidate();
            queryClient.invalidateQueries({
                queryKey: [
                    ApiConfig.queries.superAdmin.supportEmailRequests.queryKeys.detail,
                    variables.id,
                ],
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const url = `${ApiConfig.queries.superAdmin.supportEmailRequests.endpoints.delete}/${id}`;
            await axiosInstance.delete(url);
        },
        onSuccess: invalidate,
    });

    const reanalyzeMutation = useMutation({
        mutationFn: async (id: string) => {
            const url = `${ApiConfig.queries.superAdmin.supportEmailRequests.endpoints.analyze}/${id}/analyze`;
            await axiosInstance.post(url);
        },
        onSuccess: (_, id) => {
            invalidate();
            queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.superAdmin.supportEmailRequests.queryKeys.detail, id],
            });
        },
    });

    return { createMutation, updateMutation, deleteMutation, reanalyzeMutation };
};
